import { useForm } from 'react-hook-form';
import { Typography, Stack, Button, Tooltip } from '@mui/material';
import {
  TextFieldControl,
  Form,
  MASK,
  VALIDATION,
  useToast,
} from '@myhrbox/myhrbox-frontend-core';
import {
  GetUserQuery,
  useSetCandidatePhoneNumberMutation,
  useUpdateFirstCompanyCandidateMutation,
} from '@generated/graphql';
import { useTranslation } from 'react-i18next';
import { UserDataForm } from '../types';

interface UserDataProps {
  user: GetUserQuery['me'] | undefined;
}

const UserData = ({ user }: UserDataProps) => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = useToast();

  const form = useForm<UserDataForm>({
    values: {
      username: user?.candidateUsername?.telegramUsername || '',
      fullName: user?.firstCompanyCandidate?.fullName || '',
      phoneNumber: user?.phoneNumber ? user.phoneNumber.replace('+', '') : '',
      organization: user?.firstCompanyCandidate?.organization || '',
      position: user?.firstCompanyCandidate?.position || '',
      email: user?.firstCompanyCandidate?.email || '',
    },
  });

  const { handleSubmit } = form;
  const [updateFirstCompanyCandidate, { loading: candidateLoading }] =
    useUpdateFirstCompanyCandidateMutation();
  const [savePhoneNumber, { loading: phoneLoading }] = useSetCandidatePhoneNumberMutation();
  const isFormDisabled = !user?.firstCompanyCandidate;

  const onSubmit = async (formData: UserDataForm) => {
    const { dirtyFields } = form.formState;

    try {
      if (dirtyFields.phoneNumber) {
        const resPhoneNumber = await savePhoneNumber({
          variables: { phoneNumber: formData.phoneNumber },
        });
        const isPhoneNumberSaved = !!resPhoneNumber.data?.setCandidatePhoneNumber.phoneNumber;
        if (!isPhoneNumberSaved) throw new Error(t('languageSelect.errorSavingNumber'));
      }

      if (dirtyFields.email || dirtyFields.fullName) {
        const resCandidate = await updateFirstCompanyCandidate({
          variables: {
            firstCompanyCandidate: {
              fullName: formData.fullName,
              email: dirtyFields.email ? formData.email : undefined,
            },
          },
        });
        if (!resCandidate.data?.updateFirstCompanyCandidate.email)
          throw new Error(t('errors.emailBusy'));
        if (!resCandidate.data?.updateFirstCompanyCandidate.fullName)
          throw new Error(t('errors.common'));
      }
      showSuccessToast(t('successMessage'));
    } catch (error: Error | unknown) {
      const errorMessage = error instanceof Error ? error.message : t('errors.common');
      showErrorToast(errorMessage);
    }
  };

  return (
    <div>
      <Typography variant='h28' mb={{ xs: 1.5, sm: 3 }}>
        {t('user.dataTitle')}
      </Typography>
      <Form form={form}>
        <Stack spacing={3} mb={3}>
          <TextFieldControl
            name='username'
            label={t('user.usernameLabel')}
            placeholder={t('user.usernameLabel')}
            type='text'
            disabled
          />
          <TextFieldControl
            name='fullName'
            label={t('user.fullNameLabel')}
            placeholder={t('user.fullNameLabel')}
            type='text'
            rules={{
              required: true,
              ...VALIDATION({
                message: t('validation.userName'),
                maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
              }).userName,
            }}
            disabled={isFormDisabled}
          />
          <TextFieldControl
            name='phoneNumber'
            label={t('phoneNumber')}
            placeholder={t('phoneNumber')}
            type='tel'
            mask={MASK.PHONE}
            disabled={isFormDisabled}
          />
          <TextFieldControl
            name='organization'
            label={t('user.organizationLabel')}
            placeholder={t('user.organizationPlaceholder')}
            type='text'
            disabled
          />
          <TextFieldControl
            name='position'
            label={t('user.positionLabel')}
            placeholder={t('user.positionPlaceholder')}
            type='text'
            disabled
          />
          <TextFieldControl
            name='email'
            label='Email'
            placeholder='Email'
            type='email'
            rules={{
              required: true,
              ...VALIDATION({
                message: t('validation.invalidEmailCharacters'),
                maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
              }).emailFull,
            }}
            disabled={isFormDisabled}
          />
        </Stack>
      </Form>
      <Tooltip title={isFormDisabled ? t('user.notAllowed') : undefined}>
        <span>
          <Button
            variant='contained'
            size='large'
            fullWidth
            onClick={handleSubmit(onSubmit)}
            loading={candidateLoading || phoneLoading}
            disabled={isFormDisabled}>
            {t('save')}
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default UserData;
